import { useAuth } from '@teko/auth-react';
import { Container, styled } from '@teko/ui';
import { getNavItems } from 'helpers/navigation-bar';
import { useLoyaltyMemberInfo } from 'hooks/useLoyaltyMemberInfo';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { Box, Flex } from 'reflexbox';
import { useTenantConfig } from '../../contexts';

import dynamic from 'next/dynamic';

const SideNav = dynamic(() =>
  import('@teko/ecommerce').then((mod) => mod.SideNav),
);

const UserCard = dynamic(() =>
  import('@teko/ecommerce').then((mod) => mod.UserCard),
);

export interface AccountLayoutProps {
  children: React.ReactNode;
  hiddenUserNavItems?: boolean;
}
const SideBar = styled.div`
  padding-right: 1rem;
`;

const StyledContainer = styled(Container)`
  min-height: 77vh;
`;

export const AccountLayout: React.FunctionComponent<AccountLayoutProps> = ({
  children,
  hiddenUserNavItems,
}: AccountLayoutProps) => {
  const { user } = useAuth();
  const {
    tenant,
    notificationAppId,
    epicOmni948,
    epicOmni1538,
    notFetchLoyaltyMemberInfoGlobally,
    displayNewLayout,
  } = useTenantConfig();
  const router = useRouter();
  const { loyaltyMemberInfo } = useLoyaltyMemberInfo(
    notFetchLoyaltyMemberInfoGlobally,
  );
  const navItems = useMemo(() => {
    return getNavItems({
      tenant,
      logged: !!user?.userId,
      showLoyaltyItem:
        (notFetchLoyaltyMemberInfoGlobally
          ? !!user?.loyaltyMemberId
          : !!loyaltyMemberInfo) || !!epicOmni1538,
      showNotificationItem: !!notificationAppId,
      epicOmni948,
      epicOmni1546: true,
      displayNewLayout: displayNewLayout,
    });
  }, [tenant, user, loyaltyMemberInfo, epicOmni1538, epicOmni948]);

  // get all hrefs from navItems array above
  const flattedHrefs = useMemo(() => {
    return navItems.flatMap((navItem) => {
      if (!!navItem.children?.length) {
        // not return href of navItem with children since it's not a screen anymore
        return [
          ...navItem.children.map((item) => {
            return item.href;
          }),
        ];
      }
      return navItem.href;
    });
  }, [navItems]);

  // sometimes asPath from router will have multiple queries --> this will affect active menu highlight
  // find the correct href by comparing with hrefs in navItems
  const mapAsPathToActiveHref = (asPath: string) => {
    const correctHref = flattedHrefs.find((href) => asPath === href);
    return !!correctHref ? correctHref : asPath;
  };

  return (
    <StyledContainer mb={3} mt={3}>
      <Flex flexWrap="wrap">
        {!hiddenUserNavItems && (
          <Box width={[1, 1 / 3, 1 / 5]}>
            <SideBar>
              {!!user?.userId && (
                <UserCard
                  name={user?.name || user?.telephone || ''}
                  picture={user?.avatar || ''}
                />
              )}
              <SideNav
                activeHref={
                  mapAsPathToActiveHref(router.asPath) || router.pathname
                }
                navItems={navItems}
                // disable parent menu highlight when it has children & epic omni 948 is enabled
                parentMenuHighlight={!epicOmni948}
              />
            </SideBar>
          </Box>
        )}
        <Box width={hiddenUserNavItems ? [1] : [1, 2 / 3, 4 / 5]}>
          {children}
        </Box>
      </Flex>
    </StyledContainer>
  );
};
